import React, {useContext} from 'react';
import {Redirect} from 'react-router-dom';
import {Box, Typography, Grid} from '@mui/material';
import {AuthContext} from '../../../context/auth';
import {getPlanStage} from '../../../utils/productAccess';
import ClassDashboardShell from '../components/ClassDashboard.comp';
import NoDataAvailable from '../../../components/NoDataAvailable';
import CircularLoader from '../../../components/CircularLoader';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, kpiText} from '../../DashboardCommon/dashboardCards';
import CardTable from '../../DashboardCommon/CardTable.comp';

const NAVY   = '#052F44';
const ORANGE = '#FF6900';

const TITLE_TEXT = 'Learning Deployment Overview';
const SUBTITLE_TEXT = 'Learning KPIs with per-program target areas, coverage and impact signal.';

/* Slide 17 — Learning Deployment. Live data (Data Map CD-17-01 … 05):
     KPIs      CD.S_7.C_2 Programs Active (deployed / created) · C_3 Program Completion · C_5 Avg Impact Rate
     Programs  CD.S_7.C_6 Programs assigned to this class (program · creator · target area ·
               pre → post · pass rate · impact rate · students completed · signal)
   "Priority Curriculum Coverage" (CD-17-03) is not drawn — the Data Map marks it
   removed from the datahouse output by D-10. The table is drawn with the
   columns the card sends. This page used to be fixed sample data. */
const KpiCard = ({label, value, sub}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
        display: 'flex', flexDirection: 'column',
    }}>
        <Box sx={{backgroundColor: ORANGE, height: '6px', flexShrink: 0}}/>
        <Box sx={{p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', flex: 1}}>
            <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: {xs: '28px', md: '32px'}, fontWeight: 800, color: NAVY, lineHeight: 1}}>
                {value}
            </Typography>
            {sub && (
                <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.8}}>{sub}</Typography>
            )}
        </Box>
    </Box>
);

const LEARNING_DEPLOYMENT_SECTION_CODE = 'S_7';
const CARD_SECTIONS = [LEARNING_DEPLOYMENT_SECTION_CODE];

const LearningDeploymentSection = () => {
    const {authUser} = useContext(AuthContext);
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    // P0 is the baseline-only stage, before any programs exist — same gate
    // that hides this page's nav item, applied here too in case a P0 user
    // still has the URL (e.g. a stale bookmark, or the stage changes after
    // the sidebar already rendered).
    if (getPlanStage(authUser) === 'P0') {
        return <Redirect to={`/dq_pro/class-dashboard/executive-overview${window.location.search}`}/>;
    }

    if (loading) {
        return (
            <ClassDashboardShell>
                <CircularLoader />
            </ClassDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    const card = (c) => findCard(dashboardData, LEARNING_DEPLOYMENT_SECTION_CODE, c);
    const kpis = [
        {label: 'Programs Active', value: kpiText(card('C_2')), sub: 'Deployed / Created'},
        {label: 'Program Completion', value: kpiText(card('C_3'), true)},
        {label: 'Avg Impact Rate', value: kpiText(card('C_5'), true)},
    ];

    return (
        <ClassDashboardShell>
            <Box sx={{mb: 2}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2, mb: 0.3}}>
                    {TITLE_TEXT}
                </Typography>
                <Typography sx={{fontSize: '14px', color: '#4B5563', fontStyle: 'italic', mb: 0.8}}>
                    {SUBTITLE_TEXT}
                </Typography>
            </Box>

            <Typography sx={{fontSize: '18px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                Key Performance Indicators
            </Typography>

            <Grid container spacing={2.5} sx={{mb: 3}}>
                {kpis.map((k) => (
                    <Grid item xs={12} sm={4} key={k.label}>
                        <KpiCard {...k}/>
                    </Grid>
                ))}
            </Grid>

            <CardTable card={card('C_6')} emptyText="No programs assigned to this class yet." minWidth={860}/>
        </ClassDashboardShell>
    );
};

export default LearningDeploymentSection;
